// External libraries
// @ts-expect-error untyped-import
import { isObject, isString } from '@checkout/react-components';

// Internal modules
import { features } from '@/lib/features';
import { buildUrl } from './url';
import { logger } from '../logger';
import { getParams } from './params';
import { getLocale, getToken } from '../globals';
import { isFunction } from '@/lib/utils/typeUtils';
import { appStore } from '@/store';

// c3
// @ts-expect-error untyped-c3
import { getCdnHostName, getPayPalDomain } from './c3imports';
// types
import type { GenericObjectType, Noop } from '@/types';

type BuildMppUrlParams = {
  params?: GenericObjectType;
  mppPath: string;
};
/**
 * @function buildMPPUrl
 * @param {object} additionalParams
 * @param {string} mppPath
 * @returns {string}
 * @description Use to create a url to MPP app
 */
const buildMPPUrl = ({ params = {}, mppPath }: BuildMppUrlParams): string => {
  const origin = `https://www.${getPayPalDomain()}`;
  const mppMiddlePath = 'webapps/mpp';
  const { country, locale } = getLocale();
  const adjustedPath = mppPath.replace(/^\//, '');
  const additionalParams = Object.assign({}, params, { 'locale.x': locale });

  // when adjustedPath already contains webapps/mpp
  // we don't need to add it again into the path.
  const url = adjustedPath.includes(mppMiddlePath)
    ? `${origin}/${country}/${adjustedPath}`
    : `${origin}/${country}/${mppMiddlePath}/${adjustedPath}`;

  return buildUrl(url, {
    additionalParams,
    withCurrentParams: false,
  });
};

type DefaultParams = {
  path?: string;
  getDynamicPath?: () => string;
};
const TERMS_FUNCTIONS = {
  backupPayment: () => {
    const { country, locale } = getLocale();
    return `https://www.${getPayPalDomain()}/${country}/selfhelp/article/what-is-my-backup-payment-method-faq1054?locale.x=${locale}`;
  },
  DEFAULT: ({ path = '', getDynamicPath }: DefaultParams) =>
    buildMPPUrl({ mppPath: getDynamicPath ? getDynamicPath() : path }),
  feeInformation: () => {
    const { country, language, locale } = getLocale();
    return `https://${getCdnHostName()}/digitalassets/c/website/ua/pdf/${country}/${language}/defid.pdf?locale.x=${locale}`;
  },
  generateHelpLink: () => {
    const { locale } = getLocale();
    return `https://www.${getPayPalDomain()}/selfhelp/home/?locale.x=${locale}`;
  },
  rightOfWithdrawal: () => {
    const { country, locale } = getLocale();

    // Right of Withdraw checkbox is enabled just for countries
    // in TREATMENT_2 of FEATURE_SIGNUP_RIGHT_OF_WITHDRAW
    const rightOfWithdrawalCheckboxFeatureActive = features.isActive(
      'signupRightOfWithdrawalCheckbox',
    );

    if (rightOfWithdrawalCheckboxFeatureActive) {
      return `https://www.${getPayPalDomain()}/${country}/webapps/mpp/ua/right-revocation-full?locale.x=${locale}`;
    }

    return `https://www.${getPayPalDomain()}/${country}/webapps/mpp/ua/useragreement-full?locale.x=${locale}#closing-account`;
  },
};

type Config = {
  useFunction?: keyof typeof TERMS_FUNCTIONS;
} & DefaultParams;
/**
 * @function buildTermsUrl
 * @param {string} config
 * @returns {string}
 */
const buildTermsUrl = (config: Config): string => {
  const { useFunction = 'DEFAULT' } = config;
  let buildUrlFn = TERMS_FUNCTIONS[useFunction];

  if (!isFunction(buildUrlFn)) {
    logger.warn('terms_link_function_not_found', config);
    buildUrlFn = TERMS_FUNCTIONS.DEFAULT;
  }

  return buildUrlFn(config);
};

const fetchJson = async (
  ...args: Parameters<typeof fetch>
): Promise<GenericObjectType> => {
  try {
    const result = await fetch(...args);
    return result.json();
  } catch (e: unknown) {
    logger.error('weasley_error_parsing_json_response', {
      message: (e as GenericObjectType)?.message,
    });
    throw e;
  }
};

type Predicate = ([key, value]: [string, unknown]) => unknown;
/**
 * @function filterObject
 * @param {object} obj
 * @param {function} predicate
 * @returns {object}
 */
const DEFAULT_PREDICATE: Predicate = ([_, value]) => value;
const filterObject = <T extends GenericObjectType>(
  obj: T,
  predicate: Predicate = DEFAULT_PREDICATE,
): Partial<T> => {
  if (!isObject(obj)) {
    logger.error('cannot_filter_non_object', {
      type: typeof obj,
    });
  }

  if (predicate && !isFunction(predicate)) {
    logger.warn('filter_object_filter_function_is_not_a_function', {
      type: typeof predicate,
    });
    return obj;
  }

  return Object.entries(obj)
    .filter(predicate)
    .reduce(
      (accum, [key, value]) => ({
        ...accum,
        [key]: value,
      }),
      {},
    );
};

type OnceFunction = (...args: any[]) => any;

type OnceReturn<T extends OnceFunction> = (
  ...args: Parameters<T>
) => ReturnType<T> | undefined;
/**
 * @function once
 * @param {function} fn | Function to restrict to single invocation
 * @returns {function}
 */
const once = <T extends OnceFunction>(fn: T): OnceReturn<T> => {
  let hasBeenCalled = false;
  return (...args) => {
    if (!hasBeenCalled) {
      hasBeenCalled = true;
      return fn(...args);
    }
  };
};

const focusOnFirstError = (state: GenericObjectType): void => {
  const form = document.forms[0];

  if (!form) {
    return;
  }

  const fields = Array.from(form.elements);
  const fieldWithError = fields
    .map(field => field.id)
    .find(field => state[field]?.displayError);

  if (!fieldWithError) {
    return;
  }

  const el = document.getElementById(fieldWithError);
  if (el) {
    el.focus();
    if (el.tagName === 'INPUT') {
      const inputEl = el as HTMLInputElement;
      const hasSelectionRange = /^password|search|tel|text|url$/i.test(
        inputEl.type,
      );
      /**
       * `setSelectionRange()` only works for inputs of type 'password',
       * 'search', 'tel', 'text', and 'url'. Chrome will throw an error
       * if you try to use setSelectionRange on another type of input,
       * such as 'email'
       */
      if (hasSelectionRange) {
        const { length } = (el as HTMLInputElement).value;
        inputEl.setSelectionRange(length, length);
      }
    }
  }
};

/**
 * @function getAddressMetaKey
 * @param {string} country
 * @returns {string}
 * @description Takes an optional country code string and returns the key
 * where the address metadata is stored in the api store. We were originally
 * using the locale code as the key but the locale code isn't always the
 * language_country, e.g. IN can be en_GB. Also, we don't want people to be
 * confused by the format and think it is the locale so we are purposefully not
 * using the language_country format and instead using country~language
 */
const getAddressMetaKey = (country?: string): string => {
  const { country: countryCode, language } = getLocale();
  const metaKey = `${country || countryCode}~${language}`;

  return metaKey;
};

/**
 * @function getAddressMetadata
 * @param {object} metadata
 * @param {string} addressMetaKey - country code
 *
 *
 * @returns {object}
 */
const getAddressMetadata = (
  metadata: GenericObjectType,
  addressMetaKey: string,
): GenericObjectType => {
  const localeMeta = metadata[addressMetaKey];

  return localeMeta || { layout: [], strings: {} };
};

/**
 * @function getObjectProperty
 * @param {object} obj
 * @param {string} path
 * @param {object} config
 * @returns {*}
 */
// TODO: future refactor or improve typings
const getObjectProperty = (
  obj: GenericObjectType,
  path = '',
  config: GenericObjectType = {},
): unknown => {
  const { defaultValue, splitOn = '.' } = config;

  if (!isObject(obj) || !isString(splitOn) || !isString(path) || !path.length) {
    return;
  }

  const pathArray = path.split(splitOn);
  let current = obj[pathArray.shift() as string];

  while (
    current &&
    pathArray.length &&
    (isObject(current) || Array.isArray(current))
  ) {
    current = current[pathArray.shift() as string];
  }

  return pathArray.length || current === undefined ? defaultValue : current;
};

const getClientMetadataID = (defaultID?: string): string => {
  const urlParams = getParams();

  return (
    window.xprops?.clientMetadataID ||
    urlParams['client-metadata-id']?.toString() ||
    defaultID ||
    getToken()
  );
};

/**
 * @function camelToSnakeCase
 * @param {string} s
 * @returns {string}
 * @description Takes a camel case or pascal case string and returns a snake
 * case string. Useful for transforming camel/pascal strings for logging.
 */
const REG_EXP = /(?!^)([A-Z])/g;
const camelToSnakeCase = (s: string): string =>
  s.replace(REG_EXP, '_$&').toLowerCase();

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop: Noop = () => {};

/**
 * @function containsRightOfWithdrawalCheckbox
 * @description Returns if the XO contains Right of Withdrawal checkbox
 * @param {string} type The way user is transactioning. guest, signup...
 * @returns {boolean} Is Right of Withdrawal checkbox enabled
 */
const containsRightOfWithdrawalCheckbox = (
  type: 'guest' | 'signup',
): boolean => {
  return (
    type === 'signup' && features.isActive('signupRightOfWithdrawalCheckbox')
  );
};

/**
 * @function containsRightOfWithdrawalInUserAgreement
 * @description Returns if the XO contains Right of Withdrawal anchor
 *  inside User Agreement
 * @param {string} type The way user is transactioning. guest, signup...
 * @returns {boolean} Is Right of Withdrawal inside User Agreement
 */
const containsRightOfWithdrawalInUserAgreement = (
  type: 'guest' | 'signup',
): boolean => {
  return (
    type === 'signup' &&
    features.isActive('signupRightOfWithdrawalInUserAgreement')
  );
};

const isGuestOnboarding = (): boolean => {
  const { form: formState } = appStore.getState();
  return formState?.onboardOption?.value === 'guest';
};

const isPayLater = (): boolean => {
  const { ui: uiState } = appStore.getState();
  return uiState.fiType === 'bnpl';
};

export {
  buildMPPUrl,
  buildTermsUrl,
  camelToSnakeCase,
  containsRightOfWithdrawalCheckbox,
  containsRightOfWithdrawalInUserAgreement,
  fetchJson,
  filterObject,
  focusOnFirstError,
  getAddressMetadata,
  getAddressMetaKey,
  getClientMetadataID,
  getObjectProperty,
  isGuestOnboarding,
  isPayLater,
  noop,
  once,
};
